import { type RenderEvent, useAdsContext } from '@folklore/ads';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { useSetting } from '../../contexts/SiteContext';
import Ad from './Ad';

import styles from '../../styles/ads/header-ad.module.css';

// Time to wait for GPT before treating ads as blocked
const BLOCKED_DELAY = 3000;

interface HeaderAdProps {
    className?: string | null;
}

// Ad space at the top of a page, with formats of at most 100px high. It can be turned off for
// a whole site in Panneau (Sites). The height of the most common formats is reserved from the
// first render so that the page doesn't move when the ad shows up; the space only closes when
// nothing is served or when GPT doesn't load (ads blocked).
function HeaderAd({ className = null }: HeaderAdProps) {
    const enabled = useSetting('headerAd', true);
    const { ready = false } = useAdsContext() || {};
    const [empty, setEmpty] = useState(false);
    const [waitedForAds, setWaitedForAds] = useState(false);
    const onRender = ({ isEmpty = true }: RenderEvent) => setEmpty(isEmpty);

    useEffect(() => {
        if (ready) {
            return () => {};
        }
        const timeout = setTimeout(() => setWaitedForAds(true), BLOCKED_DELAY);
        return () => clearTimeout(timeout);
    }, [ready]);

    if (!enabled) {
        return null;
    }
    const blocked = !ready && waitedForAds;
    return (
        <Ad
            slot="header"
            alwaysRender
            disabled={blocked}
            onRender={onRender}
            className={classNames([
                styles.container,
                { [styles.reserved]: !empty && !blocked },
                className,
            ])}
        />
    );
}

export default HeaderAd;
